/**
 * Handoff: AI Sales offer → UnsavedPackagesProfile / PackagesProfile (existing flow).
 * Uses search_deal_raw from source_payload_json — same LDS shape as SearchAgent.
 */

function parsePayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw;
  return null;
}

function extractLdsDealIdFromDeal(deal) {
  if (!deal || typeof deal !== "object") return null;
  const top =
    deal.ldsDealId ??
    deal.dealId ??
    deal.id ??
    deal.dealFare?.ldsDealId;
  if (top != null && String(top).trim()) return String(top).trim();
  const lf = deal.linkedFlights;
  if (Array.isArray(lf) && lf[0]?.ldsDealId != null && String(lf[0].ldsDealId).trim()) {
    return String(lf[0].ldsDealId).trim();
  }
  if (lf && typeof lf === "object" && !Array.isArray(lf) && lf.ldsDealId != null) {
    const s = String(lf.ldsDealId).trim();
    if (s) return s;
  }
  return null;
}

/** כמו PackageObject לפני מעבר לפרופיל — אובייקט יחיד עם ldsDealId + flight */
function normalizeLinkedFlightsForPackagesProfile(linkedFlights) {
  if (linkedFlights == null) return null;
  if (Array.isArray(linkedFlights)) {
    return linkedFlights[0] && typeof linkedFlights[0] === "object" ? { ...linkedFlights[0] } : null;
  }
  if (typeof linkedFlights === "object") {
    return { ...linkedFlights };
  }
  return null;
}

function hasRoundTripFlightShape(lf) {
  if (!lf || !lf.flight) return false;
  const f = lf.flight;
  const arr = Array.isArray(f) ? f : [f];
  if (arr.length < 2) return false;
  const dep = arr[0]?.leg;
  const ret = arr[1]?.leg;
  const depDate = Array.isArray(dep) ? dep[0]?.depDate : dep?.depDate;
  const retDate = Array.isArray(ret) ? ret[0]?.depDate : ret?.depDate;
  return Boolean(depDate && retDate);
}

/**
 * @param {object} offer — שורת offer מה-API (כולל source_payload_json)
 * @returns {{ ok: boolean, reason?: string }}
 */
export function canOpenAiSalesOfferInPackagesProfile(offer) {
  if (!offer || typeof offer !== "object") return { ok: false, reason: "no_offer" };
  const payload = parsePayload(offer.source_payload_json);
  if (!payload || typeof payload !== "object") return { ok: false, reason: "no_payload" };
  const deal = payload.search_deal_raw;
  if (!deal || typeof deal !== "object") return { ok: false, reason: "no_search_deal" };

  const ldsId = extractLdsDealIdFromDeal(deal);
  if (!ldsId) return { ok: false, reason: "no_lds_deal_id" };

  if (!deal.hotel || typeof deal.hotel !== "object") return { ok: false, reason: "no_hotel" };

  const lf = normalizeLinkedFlightsForPackagesProfile(deal.linkedFlights);
  if (!lf) return { ok: false, reason: "no_linked_flights" };

  const lfLds = lf.ldsDealId != null && String(lf.ldsDealId).trim();
  if (!lfLds) return { ok: false, reason: "linked_flights_no_lds_id" };

  if (!hasRoundTripFlightShape(lf)) return { ok: false, reason: "incomplete_flights" };

  return { ok: true };
}

/**
 * בונה עותק ל-selectedResult תואם PackageObject → PackagesProfile.
 * @param {object} offer
 * @returns {{ ok: true, selectedResult: object } | { ok: false, reason: string }}
 */
export function buildSelectedResultFromAiSalesOffer(offer) {
  const gate = canOpenAiSalesOfferInPackagesProfile(offer);
  if (!gate.ok) return { ok: false, reason: gate.reason || "invalid" };

  const payload = parsePayload(offer.source_payload_json);
  const deal = payload.search_deal_raw;
  let selectedResult;
  try {
    selectedResult = JSON.parse(JSON.stringify(deal));
  } catch {
    return { ok: false, reason: "clone_failed" };
  }

  const ldsId = extractLdsDealIdFromDeal(selectedResult);
  if (!selectedResult.dealId && !selectedResult.id) {
    selectedResult.dealId = ldsId;
  }

  const lfNorm = normalizeLinkedFlightsForPackagesProfile(selectedResult.linkedFlights);
  selectedResult.linkedFlights = lfNorm;
  if (lfNorm && !lfNorm.ldsDealId) {
    lfNorm.ldsDealId = ldsId;
  }

  return { ok: true, selectedResult };
}
